import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { Comment, CommentStatus } from 'api/models/Comment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { dialogState } from 'state';
import CommentHistoryTable from './CommentHistoryTable';
import { mutation } from 'operations';
import { ModelTypes } from 'types/types';
import useRequest from 'hooks/useRequest';
import { getFieldsHistory, getFormsComments } from 'api';
import { Field } from 'api/models/Form';
import { commentsAtom, commentsByFieldTemplateId } from 'state/formState';
import { getCommentIcon } from 'routes/Form/components/CommentButton';

enum CommentActions {
  confirmation = 'confirmation',
  question = 'question',
  answer = 'answer',
  close = 'close',
  revertConfirmation = 'revertConfirmation',
}

function getActionByStatus(status?: CommentStatus) {
  switch (status) {
    case CommentStatus.checked:
      return [CommentActions.question, CommentActions.revertConfirmation];
    case CommentStatus.notchecked:
      return [CommentActions.question, CommentActions.confirmation];
    case CommentStatus.open:
      return [CommentActions.answer, CommentActions.close];
    case CommentStatus.answered:
      return [CommentActions.question, CommentActions.close];
    case CommentStatus.closed:
      return [CommentActions.question];
    case CommentStatus.unchecked:
    default:
      return [CommentActions.confirmation, CommentActions.question];
  }
}

function getStatusByAction(action: CommentActions) {
  switch (action) {
    case CommentActions.answer:
      return CommentStatus.answered;
    case CommentActions.close:
      return CommentStatus.closed;
    case CommentActions.confirmation:
      return CommentStatus.checked;
    case CommentActions.question:
      return CommentStatus.open;
    case CommentActions.revertConfirmation:
      return CommentStatus.notchecked;
  }
}

export default function CommentDialog() {
  const { t } = useTranslation();
  const [text, setText] = useState<string>('');
  const [{ data }, closeDialog] = useRecoilState(dialogState);
  const { value, fieldId, formId } = data;
  const comments = useRecoilValue(commentsByFieldTemplateId(fieldId));
  const comment = comments[0] as Comment | undefined;
  const [status, setStatus] = useState(comment?.status ?? CommentStatus.open);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<Field[]>([]);
  const setComments = useSetRecoilState(commentsAtom);

  const { loading: historyLoading } = useRequest(async () => {
    const history =
      (await getFieldsHistory({
        fieldTemplateId: fieldId,
        formId,
      })) ?? [];

    setHistory(history);
  });

  const onSave = async () => {
    setLoading(true);
    await mutation<Comment>({
      type: ModelTypes.comment,
      body: {
        value: value ?? '',
        fieldTemplateId: fieldId,
        formId,
        text,
        status,
        id: '',
      },
    });

    const comms = await getFormsComments({
      formId,
    });

    setComments(comms);
    setLoading(false);

    closeDialog({ isOpen: false });
  };

  const actions = getActionByStatus(comment?.status);

  return (
    <Box>
      <DialogContent>
        <Typography variant={'h5'}>
          {t('dialogs.commentsDialog.changeHistory')}
        </Typography>

        <CommentHistoryTable
          comments={comments}
          history={history}
          loading={historyLoading}
        />

        <Typography variant={'h5'} sx={{ mt: 4, mb: 2 }}>
          {t('dialogs.commentsDialog.resultOfTheCheck')}
        </Typography>

        <ToggleButtonGroup
          value={status}
          exclusive
          onChange={(e, value) => {
            setStatus(value);
          }}
          sx={{ mb: 2 }}
        >
          {actions?.map(action => {
            const status = getStatusByAction(action);
            return (
              <ToggleButton value={status}>
                <Box mr={1} mt={1}>
                  {getCommentIcon({ status, size: 'medium', color: 'black' })}
                </Box>

                {t(`dialogs.commentsDialog.actions.${action}`)}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        <TextField
          multiline
          minRows={5}
          label={t('dialogs.commentsDialog.comment')}
          fullWidth
          value={text}
          onChange={e => setText(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => closeDialog({ isOpen: false })}
          variant="contained"
          color="secondary"
        >
          {t('general.cancel')}
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="primary"
          disabled={loading || !status}
        >
          {loading ? t('general.isSaving') : t('general.save')}
        </Button>
      </DialogActions>
    </Box>
  );
}
